import { useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import styled from "@emotion/styled";
import ButtonComponent from "components/button/ButtonComponent";
import { CategoryListItem } from "features/mst_proc_fee/types/proc-fee";
import type { ProcFeeFilterSelection } from "features/mst_proc_fee/hooks/useProcFeeFilter";
import { TOption } from "type";

interface StoreDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  makerList?: TOption[];
  categoryList?: CategoryListItem[];
  selectedMaker: boolean[];
  setSelectedMaker: (selectedMaker: boolean[]) => void;
  selectedCategory: boolean[];
  setSelectedCategory: (selectedCategory: boolean[]) => void;
  handleFilter: (values?: ProcFeeFilterSelection) => void | Promise<void>;
  recommendFlag: boolean;
  setRecommendFlag: (recommendFlag: boolean) => void;
  /** 店舗 在庫 filter — persisted at parent, seeds local draft */
  stockFilterStore?: boolean;
  /** 倉庫/仕入先 在庫 filter — persisted at parent, seeds local draft */
  stockFilterDepotVendor?: boolean;
}

const CustomLabel = styled(Typography)({
  fontSize: "28px",
  fontWeight: "medium",
  color: "var(--gray-700)",
  minWidth: "120px",
});

/**
 * FilterProcFeeModal component displays a modal dialog for filtering processing fees.
 *
 * Props:
 * @param {object} props - The component props.
 * @param {boolean} props.isOpen - Controls the visibility of the modal.
 * @param {() => void} props.onClose - Callback to close the modal.
 * @param {MakerListItem[]} props.makerList - List of makers to display as filter options.
 * @param {boolean[]} props.selectedMaker - Array indicating which makers are selected.
 * @param {(selectedMaker: boolean[]) => void} props.setSelectedMaker - Callback to update selected makers.
 * @param {() => void} props.handleFilter - Callback to apply the filter.
 * @param {boolean} props.recommendFlag - Indicates if the recommended category is selected.
 * @param {(recommendFlag: boolean) => void} props.setRecommendFlag - Callback to update the recommended flag.
 *
 * Renders:
 * - Category filter (checkbox)
 * - Maker filter (checkboxes for each maker)
 * - Stock only filter (checkbox)
 * - Filter button
 */
function FilterProcFeeModal({
  isOpen,
  onClose,
  makerList,
  handleFilter,
  selectedMaker,
  setSelectedMaker,
  selectedCategory,
  setSelectedCategory,
  categoryList,
  recommendFlag,
  setRecommendFlag,
  stockFilterStore = false,
  stockFilterDepotVendor = false,
}: StoreDetailModalProps) {
  // Local draft state: only commit to parent when user clicks 絞り込み. Closing without apply discards changes.
  const [localSelectedMaker, setLocalSelectedMaker] = useState<boolean[]>(() => [
    ...(selectedMaker || []),
  ]);
  const [localSelectedCategory, setLocalSelectedCategory] = useState<boolean[]>(() => [
    ...(selectedCategory || []),
  ]);
  const [localStockFilterStore, setLocalStockFilterStore] = useState<boolean>(stockFilterStore);
  const [localStockFilterDepotVendor, setLocalStockFilterDepotVendor] =
    useState<boolean>(stockFilterDepotVendor);

  // Calculate if all makers are selected or all are deselected (from local draft)
  const isAllSelected = useMemo(() => {
    if (!makerList || makerList.length === 0) return false;
    return localSelectedMaker.every((selected) => selected === true);
  }, [localSelectedMaker, makerList]);

  const isAllDeselected = useMemo(() => {
    if (!makerList || makerList.length === 0) return true;
    return localSelectedMaker.every((selected) => selected === false);
  }, [localSelectedMaker, makerList]);

  const handleSelectAll = () => {
    if (!makerList) return;
    setLocalSelectedMaker(new Array(makerList.length).fill(true));
  };

  const handleDeselectAll = () => {
    if (!makerList) return;
    setLocalSelectedMaker(new Array(makerList.length).fill(false));
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          overflow: "auto",
          width: "80%",
          maxWidth: "870px",
          backgroundColor: "var(--white)",
          border: "3px solid var(--gray-250)",
          padding: "40px 50px",
          borderRadius: "21px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "30px",
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "fontWeightMedium",
              color: "var(--gray-700)",
              fontSize: "33px",
            }}
          >
            絞り込み
          </Typography>
          <CancelButton onClose={onClose} sx={{ color: "var(--gray-700)" }} />
        </Box>
        <hr style={{ border: "1px solid var(--brown-700)" }} />
        <Box sx={{ display: "flex", gap: "100px", mb: 4, mt: 5 }}>
          <CustomLabel>カテゴリ</CustomLabel>
          <Box sx={{ flex: 1 }}>
            <FormGroup
              sx={{ display: "flex", "& .MuiFormGroup-row": { columnGap: "65px", rowGap: "30px" } }}
              row
            >
              {categoryList?.map((maker, index) => (
                <FormControlLabel
                  key={maker.category}
                  sx={{ width: "30%" }}
                  control={
                    <Checkbox
                      checked={localSelectedCategory[index]}
                      onChange={(e) => {
                        const updated = [...localSelectedCategory];
                        updated[index] = e.target.checked;
                        setLocalSelectedCategory(updated);
                      }}
                      color="secondary"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: { xs: 16, md: 18, lg: 20 },
                        color: "var(--black)",
                        fontWeight: "regular",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "150px",
                      }}
                      title={maker.category}
                    >
                      {maker.category}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "100px", mb: 3 }}>
          <Box>
            <CustomLabel>メーカー</CustomLabel>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2, pl: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleSelectAll();
                      } else {
                        handleDeselectAll();
                      }
                    }}
                    color="secondary"
                    sx={{ p: 0, pr: 1 }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: { xs: 12, md: 16, lg: 18 },
                      color: "var(--black)",
                      fontWeight: "regular",
                    }}
                  >
                    全選択
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllDeselected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleDeselectAll();
                      } else {
                        handleSelectAll();
                      }
                    }}
                    color="secondary"
                    sx={{ p: 0, pr: 1 }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: { xs: 12, md: 16, lg: 18 },
                      color: "var(--black)",
                      fontWeight: "regular",
                    }}
                  >
                    全解除
                  </Typography>
                }
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormGroup
              sx={{ display: "flex", "& .MuiFormGroup-row": { columnGap: "65px", rowGap: "30px" } }}
              row
            >
              {makerList?.map((maker, index) => (
                <FormControlLabel
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${maker.value}-${index}`}
                  sx={{ width: "30%" }}
                  control={
                    <Checkbox
                      checked={localSelectedMaker[index]}
                      onChange={(e) => {
                        const updated = [...localSelectedMaker];
                        updated[index] = e.target.checked;
                        setLocalSelectedMaker(updated);
                      }}
                      color="secondary"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: { xs: 16, md: 18, lg: 20 },
                        color: "var(--black)",
                        fontWeight: "regular",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "150px",
                      }}
                      title={maker.label}
                    >
                      {maker.label}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "100px", mb: 3 }}>
          <CustomLabel>在庫</CustomLabel>
          <Box sx={{ flex: 1 }}>
            <FormGroup
              sx={{ display: "flex", "& .MuiFormGroup-row": { columnGap: "65px", rowGap: "30px" } }}
              row
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localStockFilterStore}
                    onChange={(e) => setLocalStockFilterStore(e.target.checked)}
                    color="secondary"
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: { xs: 16, md: 18, lg: 20 },
                      color: "var(--black)",
                      fontWeight: "regular",
                      whiteSpace: "nowrap",
                    }}
                  >
                    店舗 在庫
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localStockFilterDepotVendor}
                    onChange={(e) => setLocalStockFilterDepotVendor(e.target.checked)}
                    color="secondary"
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: { xs: 16, md: 18, lg: 20 },
                      color: "var(--black)",
                      fontWeight: "regular",
                      whiteSpace: "nowrap",
                    }}
                  >
                    倉庫/仕入先 在庫
                  </Typography>
                }
              />
            </FormGroup>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <ButtonComponent
            theme="dark"
            label="絞り込み"
            sx={{ width: "239px", mt: 0, fontSize: "25px", mx: "auto" }}
            onClick={() =>
              handleFilter({
                selectedMaker: localSelectedMaker,
                selectedCategory: localSelectedCategory,
                stockFilterStore: localStockFilterStore,
                stockFilterDepotVendor: localStockFilterDepotVendor,
              })
            }
            disabled={isAllDeselected}
          />
        </Box>
      </Box>
    </Modal>
  );
}

export default FilterProcFeeModal;
